import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { masterChartPath } from 'constants/masterChartRoutes';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Switch,
  Alert,
  Snackbar,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ArrowBackRounded,
  ContentCopyRounded,
  RefreshRounded,
  PersonRounded,
  LocalHospitalRounded,
  HealingRounded,
  MedicationRounded,
  WarningAmberRounded,
  PeopleRounded,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { AuthContext } from 'context/Auth';
import { isDevEnvironment } from 'utils/isDevEnvironment';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import {
  SHAREABLE_SECTIONS,
  createShareToken,
  buildQrPayload,
  formatManualCode,
  getTokenValidityLabel,
} from 'services/healthRecordsShareService';

// Mirror the Medical Record hub's per-section icon + color so the two screens
// read as the same record from two angles.
const SECTION_META = {
  demographics: { icon: PersonRounded, color: 'primary' },
  conditions: { icon: LocalHospitalRounded, color: 'info' },
  surgical: { icon: HealingRounded, color: 'error' },
  medications: { icon: MedicationRounded, color: 'success' },
  allergies: { icon: WarningAmberRounded, color: 'warning' },
  other: { icon: PeopleRounded, color: 'secondary' },
};

const DEFAULT_ENABLED = SHAREABLE_SECTIONS.reduce((acc, s) => {
  acc[s.id] = true;
  return acc;
}, {});

const formatRemaining = (ms) => {
  if (ms <= 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const ShareHealthRecordsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, userData, medications } = useContext(AuthContext);

  const [allowed, setAllowed] = useState(DEFAULT_ENABLED);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [share, setShare] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [copySnackbar, setCopySnackbar] = useState(false);

  const healthRecords = userData?.healthRecords ?? {};
  const sectionAvailable = useMemo(
    () => ({
      demographics: !!(healthRecords.dateOfBirth || healthRecords.sex),
      conditions: (healthRecords.pmh?.length ?? 0) > 0,
      surgical: (healthRecords.psh?.length ?? 0) > 0,
      medications: (medications?.length ?? 0) > 0,
      allergies: (healthRecords.allergies?.length ?? 0) > 0,
      other: healthRecords.other
        ? Object.values(healthRecords.other).some(
            (v) => v && String(v).trim() !== ''
          )
        : false,
    }),
    [healthRecords, medications]
  );

  useEffect(() => {
    if (!share?.expiresAtMs) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [share]);

  const remainingMs = share ? Math.max(0, share.expiresAtMs - now) : 0;
  const expired = !!share && remainingMs === 0;
  const timerColor =
    remainingMs > 120_000
      ? 'text.secondary'
      : remainingMs > 60_000
        ? 'warning.main'
        : 'error.main';

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const result = await createShareToken({
        user,
        userData,
        allowedSections: allowed,
      });
      setShare(result);
    } catch (err) {
      setError(err.message || 'Could not generate a share code. Please try again.');
    } finally {
      setGenerating(false);
    }
  }, [user, userData, allowed]);

  const handleCopyCode = useCallback(() => {
    if (!share?.manualCode || !navigator.clipboard) return;
    navigator.clipboard.writeText(formatManualCode(share.manualCode));
    setCopySnackbar(true);
  }, [share]);

  const qrValue = share ? buildQrPayload({ manualCode: share.manualCode }) : '';

  const anySelected = Object.values(allowed).some(Boolean);

  return (
    <div className="inner">
      <Box sx={{ mt: { xs: 1, sm: 5 }, mb: 1 }}>
        <Button
          startIcon={<ArrowBackRounded />}
          onClick={() => navigate(masterChartPath())}
          size="small"
        >
          Back to Medical Record
        </Button>
      </Box>

      <DashboardPageHeader
        title="Share with a provider"
        subtitle={`Pick what to share and we'll create a single-use code. It expires in ${getTokenValidityLabel()}.`}
        sx={{ mt: 0 }}
      />

      <Alert severity="warning" sx={{ mb: 2 }}>
        Please only share this code with your own healthcare provider. Anyone who
        scans it can view the medical information you choose to include.
      </Alert>

      {!share ? (
        <>
          <Paper variant="outlined" sx={{ mb: 2 }}>
            {SHAREABLE_SECTIONS.map((section, i) => {
              const available = sectionAvailable[section.id];
              const meta = SECTION_META[section.id] ?? SECTION_META.demographics;
              const Icon = meta.icon;
              const palette = theme.palette[meta.color] ?? theme.palette.primary;
              return (
                <React.Fragment key={section.id}>
                  {i > 0 && <Divider />}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1.5,
                      px: 2,
                      opacity: available ? 1 : 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        borderRadius: 1.5,
                        bgcolor: alpha(palette.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon sx={{ fontSize: 20, color: `${meta.color}.main` }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {section.label}
                      </Typography>
                      {!available && (
                        <Typography variant="caption" color="text.secondary">
                          Nothing added yet
                        </Typography>
                      )}
                    </Box>
                    <Switch
                      edge="end"
                      checked={!!allowed[section.id] && available}
                      disabled={!available}
                      onChange={(e) =>
                        setAllowed((prev) => ({
                          ...prev,
                          [section.id]: e.target.checked,
                        }))
                      }
                    />
                  </Box>
                </React.Fragment>
              );
            })}
          </Paper>

          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <LoadingButton
            variant="contained"
            onClick={handleGenerate}
            loading={generating}
            disabled={!anySelected}
          >
            Generate code
          </LoadingButton>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
            alignItems: { xs: 'flex-start', sm: 'center' },
          }}
        >
            <Box sx={{ flexShrink: 0 }}>
              <Box
                sx={{
                  p: 2,
                  lineHeight: 0,
                  bgcolor: 'common.white',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <QRCodeSVG value={qrValue} size={256} level="H" />
              </Box>
              {isDevEnvironment() && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    mt: 1,
                    maxWidth: 288,
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                  }}
                >
                  DEBUG (dev only) — QR payload: {qrValue}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Have your care team scan this code, or read them the code below
                to enter by hand.
              </Typography>

              <Typography variant="overline" color="text.secondary">
                Code
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h4" fontWeight={700} letterSpacing={2}>
                  {formatManualCode(share.manualCode)}
                </Typography>
                <IconButton onClick={handleCopyCode} aria-label="Copy code">
                  <ContentCopyRounded fontSize="small" />
                </IconButton>
              </Stack>

              <Typography variant="body2" sx={{ color: timerColor, mt: 0.5, mb: 2 }}>
                {expired ? 'Expired' : `Expires in ${formatRemaining(remainingMs)}`}
              </Typography>

              {expired && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This code has expired. Please generate a new one.
                </Alert>
              )}

              <LoadingButton
                variant={expired ? 'contained' : 'outlined'}
                onClick={handleGenerate}
                loading={generating}
                startIcon={<RefreshRounded />}
              >
                New code
              </LoadingButton>
            </Box>
          </Box>
      )}

      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="Code copied"
      />
    </div>
  );
};

export default ShareHealthRecordsPage;
